import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import TidioController from "common/utils/TidioController";
import { isMobileApp } from "common/utils/browser";
import { RefinementView } from "./RefinementView";
import { AppLayout } from "./components/AppLayout";
import { DesignOptionsProvider, useDesignOptions } from "./contexts/DesignOptionsContext";
import { useImageGeneration } from "./hooks/useImageGeneration";
import { useMobileDetection } from "./hooks/useMobileDetection";
import {
  trackAiStudioArtStyleSelected,
  trackAiStudioColorModeSelected,
  trackAiStudioDesignTypeSelected,
  trackAiStudioOrientationSelected
} from "./lib/analytics";
import type { GenerateImageParams } from "./types";

type AppProps = {
  productCatalogNumber?: string | null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      experimental_prefetchInRender: true,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: Infinity
    },
    mutations: {
      onMutate: context => context
    }
  }
});

export function App({ productCatalogNumber }: AppProps) {
  return (
    <DesignOptionsProvider>
      <QueryClientProvider client={queryClient}>
        <AppContent productCatalogNumber={productCatalogNumber} />
      </QueryClientProvider>
    </DesignOptionsProvider>
  );
}

type AppContentProps = {
  productCatalogNumber?: string | null | undefined;
};

function AppContent({ productCatalogNumber }: AppContentProps) {
  const { isMobile, isMobileMenuOpen, openMobileMenu, closeMobileMenu } = useMobileDetection();

  const {
    prompt,
    isGenerating,
    generatedImages,
    error,
    editingImage,
    uploadedImageForEditing,
    isRefining,
    imageTransitioning,
    selectingImageUrl,
    setPrompt,
    generateImages,
    selectImage,
    startEditing,
    startEditingUploadedImage,
    exitEditing,
    refineImage,
    clearError,
    setError
  } = useImageGeneration(productCatalogNumber);

  const { selectedTemplate, selectedStyle, selectedOrientation, selectedColors } =
    useDesignOptions();

  const hasInitialized = useRef(false);
  const tidioControllerRef = useRef<TidioController | null>(null);

  useEffect(() => {
    if (!hasInitialized.current) return;

    const templateName = selectedTemplate === null ? "default" : selectedTemplate;
    trackAiStudioDesignTypeSelected({
      designType: templateName,
      prompt,
      generatedDesignUrls: generatedImages.map(img => img.url)
    });
  }, [selectedTemplate]);

  useEffect(() => {
    if (!hasInitialized.current) return;

    const styleName = selectedStyle === null ? "default" : selectedStyle;
    trackAiStudioArtStyleSelected({
      artStyle: styleName,
      prompt,
      generatedDesignUrls: generatedImages.map(img => img.url)
    });
  }, [selectedStyle]);

  useEffect(() => {
    if (!hasInitialized.current) return;

    trackAiStudioOrientationSelected({
      orientation: selectedOrientation,
      prompt,
      generatedDesignUrls: generatedImages.map(img => img.url)
    });
  }, [selectedOrientation]);

  useEffect(() => {
    if (!hasInitialized.current) return;

    trackAiStudioColorModeSelected({
      colorMode: selectedColors,
      prompt,
      generatedDesignUrls: generatedImages.map(img => img.url)
    });
  }, [selectedColors]);

  useEffect(() => {
    hasInitialized.current = true;
  }, []);

  // Manage TidioController initialization and cleanup
  useEffect(() => {
    if (!isMobileApp()) {
      return () => {}; // Return empty cleanup function for consistency
    }

    // Cleanup previous instance if it exists
    if (tidioControllerRef.current) {
      tidioControllerRef.current.destroy?.();
      tidioControllerRef.current = null;
    }

    // Determine target position based on editing state
    const targetPosition = editingImage !== null ? "160px" : "90px";

    // Create and initialize new TidioController
    tidioControllerRef.current = new TidioController({
      targetPosition
    });
    tidioControllerRef.current.initialize();

    // Cleanup function
    return () => {
      if (tidioControllerRef.current) {
        tidioControllerRef.current.destroy?.();
        tidioControllerRef.current = null;
      }
    };
  }, [editingImage]); // Re-run when editing state changes

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    const params: GenerateImageParams = {
      prompt,
      selectedTemplate: selectedTemplate === "default" ? null : selectedTemplate,
      selectedStyle: selectedStyle === "default" ? null : selectedStyle,
      selectedOrientation,
      selectedColors
    };

    await generateImages(params);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Show refinement view when editing an image
  if (editingImage !== null || uploadedImageForEditing !== null) {
    const currentImage =
      editingImage !== null ? generatedImages[editingImage] : uploadedImageForEditing;

    if (!currentImage) return null;

    return (
      <div onContextMenu={handleContextMenu}>
        <RefinementView
          image={currentImage}
          selectedOrientation={selectedOrientation}
          onRefineImage={refineImage}
          onSelectImage={selectImage}
          onExitEditing={exitEditing}
          isRefining={isRefining}
          imageTransitioning={imageTransitioning}
          selectingImageUrl={selectingImageUrl}
          error={error}
          onErrorDismiss={clearError}
          isMobile={isMobile}
        />
      </div>
    );
  }

  return (
    <div onContextMenu={handleContextMenu}>
      <AppLayout
        generatedImages={generatedImages}
        isGenerating={isGenerating}
        selectingImageUrl={selectingImageUrl}
        error={error}
        prompt={prompt}
        onPromptChange={setPrompt}
        onGenerate={handleGenerate}
        onSelectImage={selectImage}
        onStartEditing={startEditing}
        onStartEditingUploadedImage={startEditingUploadedImage}
        isMobile={isMobile}
        isMobileMenuOpen={isMobileMenuOpen}
        onOpenMobileMenu={openMobileMenu}
        onCloseMobileMenu={closeMobileMenu}
        onErrorDismiss={clearError}
        onUploadError={setError}
      />
    </div>
  );
}
